const PREFIX = 'rmc1:';

let cachedCryptoKey = null;

const getSecret = () => process.env.REACT_APP_RESTRICTED_MESSAGE_KEY || '';

const bytesToBase64 = (bytes) => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const base64ToBytes = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const deriveKey = async () => {
  if (cachedCryptoKey) {
    return cachedCryptoKey;
  }

  const secret = getSecret();
  if (!secret) {
    throw new Error('REACT_APP_RESTRICTED_MESSAGE_KEY is not configured.');
  }

  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(secret));
  cachedCryptoKey = await crypto.subtle.importKey(
    'raw',
    hash,
    { name: 'AES-CBC' },
    false,
    ['encrypt', 'decrypt']
  );

  return cachedCryptoKey;
};

export const isEncryptedRestrictedMessage = (value) =>
  typeof value === 'string' && value.startsWith(PREFIX);

/**
 * Encrypt blocked-keyword original text (same algorithm as Laravel RestrictedMessageCipher).
 */
export const encryptRestrictedMessage = async (plaintext) => {
  if (plaintext == null || plaintext === '') {
    return null;
  }

  if (isEncryptedRestrictedMessage(plaintext)) {
    return plaintext;
  }

  const key = await deriveKey();
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, encoded);
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return `${PREFIX}${bytesToBase64(combined)}`;
};

/**
 * Decrypt blocked-keyword original text from API responses.
 */
export const decryptRestrictedMessage = async (payload) => {
  if (payload == null || payload === '') {
    return null;
  }

  if (!isEncryptedRestrictedMessage(payload)) {
    return payload;
  }

  if (!getSecret()) {
    console.warn('REACT_APP_RESTRICTED_MESSAGE_KEY is not configured; cannot decrypt original_message.');
    return null;
  }

  try {
    const encoded = payload.slice(PREFIX.length);
    const raw = base64ToBytes(encoded);
    const iv = raw.slice(0, 16);
    const ciphertext = raw.slice(16);
    const key = await deriveKey();
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, ciphertext);

    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.warn('Restricted message decryption failed', error);
    return null;
  }
};

export const hasRestrictedOriginalMessage = (message) =>
  Boolean(message?.keyword_blocked || message?.original_message);

/**
 * Resolve original blocked text for UI/copy (decrypts when needed).
 */
export const resolveOriginalMessageForCopy = async (message) => {
  const candidate = message?.original_message;
  if (!candidate) {
    return '';
  }

  const decrypted = await decryptRestrictedMessage(candidate);
  return decrypted || '';
};

/**
 * Decrypt original_message on a single chat message object.
 */
export const withDecryptedRestrictedFields = async (message) => {
  if (!message?.original_message) {
    return message;
  }

  const decrypted = await decryptRestrictedMessage(message.original_message);
  if (decrypted) {
    return {
      ...message,
      original_message: decrypted,
    };
  }

  return {
    ...message,
    keyword_blocked: message.keyword_blocked ?? isEncryptedRestrictedMessage(message.original_message),
  };
};

/**
 * Decrypt original_message across a timeline array (skips date/system rows).
 */
export const decryptRestrictedMessagesInList = async (messages) =>
  Promise.all(
    (messages || []).map(async (message) => {
      if (!message || message.msgType === 'date' || message.msgType === 'system') {
        return message;
      }

      return withDecryptedRestrictedFields(message);
    })
  );
